/**
 * Mobile profile tab — cart-style drawer with inline shopify-account.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
  const ACCOUNT_HOME_SELECTOR = '.account-button';
  const ACCOUNT_SLOT_SELECTOR = '[data-mobile-account-slot]';
  const ACCOUNT_FALLBACK_SELECTOR = '[data-mobile-account-fallback]';
  const DRAWER_ID = 'mobile-account-drawer';
  const PENDING_CLASS = 'mobile-bottom-nav__item--pending';
  const LOADING_CLASS = 'is-loading';
  const FALLBACK_CLASS = 'is-fallback';
  const SLOT_CONCEALED_CLASS = 'mobile-account-drawer__slot--concealed';
  const PREMIUM_STYLE_ATTR = 'data-rh-account-premium';

  /** @type {string} */
  const ACCOUNT_PREMIUM_CSS = `
    a, button {
      font-family: inherit;
    }
    a[href*="orders"], a[href*="profile"], a[href*="addresses"] {
      border-radius: 12px;
      transition: background 0.2s ease;
    }
    a[href*="orders"]:hover, a[href*="profile"]:hover, a[href*="addresses"]:hover {
      background: rgba(0, 168, 120, 0.08);
    }
    [class*="order" i], [data-order], li[class*="order" i] {
      border-radius: 12px !important;
      border: 1px solid rgba(0, 0, 0, 0.08) !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
      margin-block: 8px !important;
      overflow: hidden;
    }
    input, select, textarea {
      border-radius: 10px !important;
      border-color: rgba(0, 0, 0, 0.12) !important;
    }
    input:focus, select:focus, textarea:focus {
      outline: 2px solid rgba(0, 168, 120, 0.35) !important;
      outline-offset: 1px;
    }
    button[type="submit"], button[class*="primary" i], a[class*="button" i] {
      border-radius: 999px !important;
      background: #00a878 !important;
      color: #fff !important;
      font-weight: 600 !important;
    }
    h1, h2, h3, [role="heading"] {
      color: #111827;
      font-weight: 700;
    }
  `;

  /** @type {number} */
  let openGuardUntil = 0;

  /** @type {boolean} */
  let accountMountedInDrawer = false;

  /** @type {Promise<HTMLElement | null> | null} */
  let warmPromise = null;

  /** @returns {HTMLElement | null} */
  function getAccountElement() {
    return document.querySelector('shopify-account');
  }

  /** @returns {import('@theme/theme-drawer').ThemeDrawer | null} */
  function getAccountDrawer() {
    const drawer = document.getElementById(DRAWER_ID);
    return drawer && typeof drawer.open === 'function' ? drawer : null;
  }

  /** @returns {ShadowRoot | null} */
  function getAccountRoot(account) {
    return account?.shadowRoot ?? null;
  }

  /** @param {boolean} isLoading */
  function setDrawerLoading(isLoading) {
    const drawer = getAccountDrawer();
    if (!(drawer instanceof HTMLElement)) return;
    drawer.classList.toggle(LOADING_CLASS, isLoading);
  }

  /** @param {boolean} concealed */
  function setAccountSlotConcealed(concealed) {
    const slot = document.querySelector(ACCOUNT_SLOT_SELECTOR);
    if (!(slot instanceof HTMLElement)) return;
    slot.classList.toggle(SLOT_CONCEALED_CLASS, concealed);
  }

  /** @param {boolean} showFallback */
  function setDrawerFallback(showFallback) {
    const drawer = getAccountDrawer();
    const fallback = document.querySelector(ACCOUNT_FALLBACK_SELECTOR);

    if (drawer instanceof HTMLElement) {
      drawer.classList.toggle(FALLBACK_CLASS, showFallback);
    }

    if (showFallback) {
      setAccountSlotConcealed(true);
    }

    if (fallback instanceof HTMLElement) {
      fallback.hidden = !showFallback;
    }
  }

  /** @param {HTMLElement | null | undefined} trigger @param {boolean} isPending */
  function setTriggerPending(trigger, isPending) {
    if (!(trigger instanceof HTMLElement)) return;
    trigger.classList.toggle(PENDING_CLASS, isPending);
    trigger.setAttribute('aria-busy', isPending ? 'true' : 'false');
  }

  /** @param {ParentNode} root */
  function hideShopLoginSkeletonNodes(root) {
    root.querySelectorAll('*').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      const className = node.className?.toString?.() ?? '';
      const isSkeletonLike =
        /skeleton|placeholder|shimmer|loading/i.test(className) ||
        node.getAttribute('aria-busy') === 'true';

      if (!isSkeletonLike) return;
      if (node.matches('button, a[href], input, shop-login')) return;

      node.style.display = 'none';
      node.dataset.mobileDrawerHidden = 'true';
    });
  }

  /** @param {HTMLElement} account */
  function getShopLoginElement(account) {
    return getAccountRoot(account)?.querySelector('shop-login') ?? null;
  }

  /** @param {HTMLElement | null | undefined} shopLogin */
  function getShopLoginRoot(shopLogin) {
    return shopLogin?.shadowRoot ?? null;
  }

  /** @param {HTMLElement | null | undefined} shopLogin */
  function isShopLoginButtonVisible(shopLogin) {
    const shopRoot = getShopLoginRoot(shopLogin);
    if (!shopRoot) return false;

    const shopButton = shopRoot.querySelector('button:not([disabled]), a[href]');
    if (!(shopButton instanceof HTMLElement)) return false;

    return getComputedStyle(shopButton).display !== 'none';
  }

  /** @param {HTMLElement} account */
  function configureShopLoginForDrawer(account) {
    const shopLogin = getShopLoginElement(account);
    if (!(shopLogin instanceof HTMLElement)) return;

    if (shopLogin.getAttribute('presentation-mode') !== 'button') {
      shopLogin.setAttribute('presentation-mode', 'button');
    }
  }

  /** @param {HTMLElement} account */
  function stripShopLoginSkeletons(account) {
    configureShopLoginForDrawer(account);

    const shopLogin = getShopLoginElement(account);
    const shopRoot = getShopLoginRoot(shopLogin);
    if (!shopRoot) return;

    hideShopLoginSkeletonNodes(shopRoot);

    shopRoot.querySelectorAll('[class*="card" i]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      if (!card.querySelector('button, a[href]')) return;

      card.style.border = 'none';
      card.style.boxShadow = 'none';
      card.style.background = 'transparent';
      card.style.padding = '0';
    });

    shopRoot.querySelectorAll('[role="status"], [aria-live]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (/loading|skeleton/i.test(node.textContent ?? '')) {
        node.style.display = 'none';
        node.dataset.mobileDrawerHidden = 'true';
      }
    });
  }

  /** @param {HTMLElement} account */
  function isShopLoginPresentable(account) {
    stripShopLoginSkeletons(account);

    const shopLogin = getShopLoginElement(account);
    if (!isShopLoginButtonVisible(shopLogin)) return false;

    const shopRoot = getShopLoginRoot(shopLogin);
    if (!shopRoot) return false;

    return (
      shopRoot.querySelector(
        '[class*="skeleton" i]:not([data-mobile-drawer-hidden]), [class*="placeholder" i]:not([data-mobile-drawer-hidden])'
      ) == null
    );
  }

  /** @param {HTMLElement} account @returns {Promise<boolean>} */
  async function waitForShopLoginPresentable(account, timeoutMs = 8000) {
    const started = Date.now();

    while (Date.now() - started < timeoutMs) {
      configureShopLoginForDrawer(account);
      stripShopLoginSkeletons(account);

      if (isShopLoginPresentable(account)) {
        return true;
      }

      await new Promise((resolve) => window.requestAnimationFrame(resolve));
    }

    stripShopLoginSkeletons(account);
    return isShopLoginButtonVisible(getShopLoginElement(account));
  }

  /** @param {HTMLElement} account */
  function isAccountSurfaceOpen(account) {
    const root = getAccountRoot(account);
    if (!root) return false;

    return (
      root.querySelector('dialog[open], [popover]:popover-open') != null ||
      root.querySelector('a[href], input, button:not([hidden])') != null
    );
  }

  /** @param {HTMLElement} account @returns {Promise<HTMLElement>} */
  function waitForAccountSurface(account, timeoutMs = 10000) {
    if (isAccountSurfaceOpen(account)) {
      return Promise.resolve(account);
    }

    return new Promise((resolve) => {
      /** @type {number | undefined} */
      let timeoutId;

      /** @type {MutationObserver | undefined} */
      let observer;

      const finish = () => {
        if (timeoutId) window.clearTimeout(timeoutId);
        observer?.disconnect();
        resolve(account);
      };

      const observeRoot = (/** @type {ShadowRoot | HTMLElement} */ root) => {
        observer = new MutationObserver(() => {
          if (isAccountSurfaceOpen(account)) finish();
        });

        observer.observe(root, { childList: true, subtree: true, attributes: true });
      };

      if (account.shadowRoot) {
        observeRoot(account.shadowRoot);
      } else {
        observer = new MutationObserver(() => {
          if (account.shadowRoot) {
            observer?.disconnect();
            observeRoot(account.shadowRoot);
          }
        });

        observer.observe(account, { childList: true });
      }

      timeoutId = window.setTimeout(finish, timeoutMs);
    });
  }

  /** @returns {Promise<HTMLElement | null>} */
  function warmAccountComponent() {
    if (!MOBILE_MQ.matches) {
      return Promise.resolve(getAccountElement());
    }

    if (warmPromise) return warmPromise;

    warmPromise = (async () => {
      await customElements.whenDefined('theme-drawer');
      await customElements.whenDefined('shopify-account');
      return getAccountElement();
    })().catch(() => {
      warmPromise = null;
      return getAccountElement();
    });

    return warmPromise;
  }

  /** @param {HTMLElement} account */
  function markAccountDrawerHost(account) {
    account.dataset.mobileDrawerHost = 'true';
  }

  /** @param {HTMLElement} account */
  function clearAccountDrawerHost(account) {
    delete account.dataset.mobileDrawerHost;
  }

  /** @param {HTMLElement} account */
  function resetAccountDialogStyles(account) {
    const root = getAccountRoot(account);
    if (!root) return;

    root.querySelectorAll('[data-mobile-drawer-hidden]').forEach((node) => {
      if (node instanceof HTMLElement) node.style.removeProperty('display');
      node.removeAttribute('data-mobile-drawer-hidden');
    });

    const shopLogin = getShopLoginElement(account);
    getShopLoginRoot(shopLogin)
      ?.querySelectorAll('[data-mobile-drawer-hidden]')
      .forEach((node) => {
        if (node instanceof HTMLElement) node.style.removeProperty('display');
        node.removeAttribute('data-mobile-drawer-hidden');
      });
  }

  /** @param {HTMLElement} account */
  function injectAccountPremiumStyles(account) {
    const root = getAccountRoot(account);
    if (!root || root.querySelector(`style[${PREMIUM_STYLE_ATTR}]`)) return;

    const style = document.createElement('style');
    style.setAttribute(PREMIUM_STYLE_ATTR, 'true');
    style.textContent = ACCOUNT_PREMIUM_CSS;
    root.appendChild(style);
  }

  /**
   * @param {HTMLElement} account
   * @param {'orders' | 'profile'} route
   * @returns {boolean}
   */
  function navigateAccountRouteInDrawer(account, route) {
    const root = getAccountRoot(account);
    if (!root) return false;

    const selector =
      route === 'orders'
        ? 'a[href*="orders"], button[href*="orders"], [data-path*="orders"]'
        : 'a[href*="profile"], button[href*="profile"], [data-path*="profile"]';

    const link = root.querySelector(selector);
    if (!(link instanceof HTMLElement)) return false;

    link.click();
    return true;
  }

  /** @param {Event} event */
  function handleQuickNavClick(event) {
    if (!MOBILE_MQ.matches) return;

    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const route = trigger.dataset.mobileAccountRoute;
    if (route !== 'orders' && route !== 'profile') return;

    event.preventDefault();

    const fallbackUrl = trigger.getAttribute('href');

    openAccountDrawer(fallbackUrl ?? undefined, null).then(() => {
      const account = getAccountElement();
      if (!account) {
        if (fallbackUrl) window.location.href = fallbackUrl;
        return;
      }

      window.setTimeout(() => {
        injectAccountPremiumStyles(account);
        const navigated = navigateAccountRouteInDrawer(account, route);
        if (!navigated && fallbackUrl) window.location.href = fallbackUrl;
      }, 300);
    });
  }

  /** @param {HTMLElement} account */
  function flattenAccountDialog(account) {
    const root = getAccountRoot(account);
    if (!root) return;

    root
      .querySelectorAll('button[popovertarget], button[aria-haspopup="dialog"]')
      .forEach((trigger) => {
        if (!(trigger instanceof HTMLElement)) return;
        trigger.style.display = 'none';
        trigger.dataset.mobileDrawerHidden = 'true';
      });

    const dialog = root.querySelector('dialog[open], dialog');
    if (!(dialog instanceof HTMLElement)) return;

    const headerRegion =
      dialog.querySelector('.account__header-region') ??
      dialog.querySelector('.account__header') ??
      dialog.querySelector('header');

    if (headerRegion instanceof HTMLElement) {
      headerRegion.style.display = 'none';
      headerRegion.dataset.mobileDrawerHidden = 'true';
    }

    const nestedClose =
      dialog.querySelector('#close-button') ??
      dialog.querySelector('button.account__close') ??
      dialog.querySelector('button[aria-label*="lose" i]') ??
      dialog.querySelector('button[aria-label*="Close" i]');

    if (nestedClose instanceof HTMLElement) {
      nestedClose.style.display = 'none';
      nestedClose.dataset.mobileDrawerHidden = 'true';
    }

    dialog.querySelectorAll('h1, h2, [role="heading"]').forEach((heading) => {
      if (!(heading instanceof HTMLElement)) return;
      heading.style.display = 'none';
      heading.dataset.mobileDrawerHidden = 'true';
    });
  }

  /** @returns {HTMLElement | null} */
  function getAccountTriggerButton(account) {
    const root = getAccountRoot(account);
    if (!root) return null;

    return (
      root.querySelector('button[popovertarget]') ??
      root.querySelector('button[aria-haspopup]') ??
      root.querySelector('button')
    );
  }

  /** @returns {boolean} */
  function isAccountOpen(account) {
    const root = getAccountRoot(account);
    if (!root) return false;

    const dialog = root.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement && dialog.open) return true;

    return root.querySelector('[popover]:popover-open') != null;
  }

  /** @param {HTMLElement} account @returns {boolean} */
  function openAccountSurface(account) {
    const root = getAccountRoot(account);
    if (!root) return false;

    if (isAccountOpen(account) && isAccountSurfaceOpen(account)) {
      return true;
    }

    const inDrawer = account.closest(ACCOUNT_SLOT_SELECTOR) != null;
    const dialog = root.querySelector('dialog');

    if (dialog instanceof HTMLDialogElement) {
      if (inDrawer) {
        if (!dialog.open) dialog.show();
      } else if (!dialog.open) {
        dialog.showModal();
      }
      return true;
    }

    const popover = root.querySelector('[popover]');
    if (popover instanceof HTMLElement && typeof popover.showPopover === 'function') {
      if (!popover.matches(':popover-open')) popover.showPopover();
      return true;
    }

    const trigger = getAccountTriggerButton(account);
    if (trigger instanceof HTMLElement) {
      trigger.click();
      return true;
    }

    return false;
  }

  /** @param {HTMLElement} account @returns {Promise<boolean>} */
  async function revealAccountSurface(account) {
    for (let attempt = 0; attempt < 40; attempt += 1) {
      openAccountSurface(account);
      await waitForAccountSurface(account, 400);
      if (isAccountSurfaceOpen(account)) return true;
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
    }

    return isAccountSurfaceOpen(account);
  }

  /** @returns {boolean} */
  function closeAccountSurface(account) {
    const root = getAccountRoot(account);
    if (!root) return false;

    const dialog = root.querySelector('dialog[open]');
    if (dialog instanceof HTMLDialogElement) {
      dialog.close();
      return true;
    }

    const popover = root.querySelector('[popover]:popover-open');
    if (popover instanceof HTMLElement && typeof popover.hidePopover === 'function') {
      popover.hidePopover();
      return true;
    }

    return false;
  }

  function showAccountSlot() {
    setAccountSlotConcealed(false);
    setDrawerFallback(false);
  }

  /**
   * @param {{ reveal?: boolean }} [options]
   * @returns {boolean}
   */
  function mountAccountInDrawer(options = {}) {
    const reveal = options.reveal !== false;
    const home = document.querySelector(ACCOUNT_HOME_SELECTOR);
    const slot = document.querySelector(ACCOUNT_SLOT_SELECTOR);
    const account = getAccountElement();

    if (!home || !slot || !account) return false;

    if (!slot.contains(account)) {
      markAccountDrawerHost(account);
      slot.appendChild(account);
    }

    configureShopLoginForDrawer(account);
    accountMountedInDrawer = true;
    setAccountSlotConcealed(!reveal);

    const root = getAccountRoot(account);
    if (root) {
      const modeObserver = new MutationObserver(() => {
        configureShopLoginForDrawer(account);
        stripShopLoginSkeletons(account);
      });
      modeObserver.observe(root, { childList: true, subtree: true, attributes: true });
      window.setTimeout(() => modeObserver.disconnect(), 10000);
    }

    if (reveal) showAccountSlot();
    return true;
  }

  function restoreAccountToHeader() {
    if (!MOBILE_MQ.matches || accountMountedInDrawer) return;

    const home = document.querySelector(ACCOUNT_HOME_SELECTOR);
    const account = getAccountElement();

    if (!home || !account || home.contains(account)) return;

    closeAccountSurface(account);
    resetAccountDialogStyles(account);
    clearAccountDrawerHost(account);
    home.appendChild(account);
  }

  /** @param {HTMLElement} account */
  async function finalizeAccountInDrawer(account) {
    configureShopLoginForDrawer(account);
    flattenAccountDialog(account);
    injectAccountPremiumStyles(account);
    await waitForShopLoginPresentable(account, 8000);
    stripShopLoginSkeletons(account);
  }

  /**
   * @param {string | undefined} fallbackUrl
   * @param {HTMLElement | null | undefined} trigger
   */
  async function openAccountDrawer(fallbackUrl, trigger) {
    if (!MOBILE_MQ.matches) {
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    await customElements.whenDefined('theme-drawer');

    const drawer = getAccountDrawer();
    if (!drawer) {
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    setTriggerPending(trigger, true);
    setDrawerFallback(false);

    if (drawer.isOpen) {
      try {
        const account = await warmAccountComponent();
        if (account) {
          mountAccountInDrawer({ reveal: false });
          await revealAccountSurface(account);
          await finalizeAccountInDrawer(account);
          showAccountSlot();
        }
      } finally {
        setTriggerPending(trigger, false);
        setDrawerLoading(false);
      }
      return;
    }

    openGuardUntil = Date.now() + 400;
    setDrawerLoading(true);
    drawer.open();

    try {
      const account = await warmAccountComponent();

      if (!account || !mountAccountInDrawer({ reveal: false })) {
        setDrawerFallback(true);
        return;
      }

      const opened = await revealAccountSurface(account);

      if (!opened) {
        setDrawerFallback(true);
        return;
      }

      await finalizeAccountInDrawer(account);
      showAccountSlot();
    } finally {
      setDrawerLoading(false);
      setTriggerPending(trigger, false);
    }
  }

  function handleDrawerClose() {
    if (Date.now() < openGuardUntil) return;

    const account = getAccountElement();
    if (account) closeAccountSurface(account);

    setDrawerLoading(false);
    setDrawerFallback(false);
    setAccountSlotConcealed(true);

    if (!MOBILE_MQ.matches) {
      accountMountedInDrawer = false;
      restoreAccountToHeader();
    }
  }

  /** @param {Event} event */
  function handleProfileActivate(event) {
    if (!MOBILE_MQ.matches) return;

    event.preventDefault();
    event.stopPropagation();

    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    openAccountDrawer(trigger.dataset.fallbackUrl, trigger);
  }

  /** @param {Event} event */
  function handleProfileWarm(event) {
    if (!MOBILE_MQ.matches) return;
    warmAccountComponent();
  }

  function init() {
    document.querySelectorAll('[data-mobile-account-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;

      trigger.addEventListener('touchstart', handleProfileWarm, { passive: true });
      trigger.addEventListener('click', handleProfileActivate);
    });

    document.querySelectorAll('[data-mobile-account-route]').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;
      link.addEventListener('click', handleQuickNavClick);
    });

    document.addEventListener('theme-drawer:close', (event) => {
      if (/** @type {Element} */ (event.target).id !== DRAWER_ID) return;
      handleDrawerClose();
    });

    MOBILE_MQ.addEventListener('change', () => {
      if (!MOBILE_MQ.matches) {
        warmPromise = null;
        accountMountedInDrawer = false;
        handleDrawerClose();
        restoreAccountToHeader();
        return;
      }

      warmAccountComponent();
    });

    if (MOBILE_MQ.matches) {
      warmAccountComponent();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
